/* ============================================================================
 * ALTIORA — GlobalLocationSearch
 * ----------------------------------------------------------------------------
 * Autocomplete de lieux mondial (Nominatim / OpenStreetMap · gratuit, sans clé).
 *
 * Usage : consultation d'un client où qu'il soit dans le monde. Le
 * professionnel (ou le membre) tape « Casablanca », « Singapore »,
 * « 15 rue de la Paix Paris »… et on obtient les coordonnées + le nom
 * canonique.
 *
 * Note doctrinale (Principe 6 · explicabilité) : la source Nominatim / OSM
 * est indiquée explicitement à l'utilisateur.
 * ==========================================================================*/
import { useEffect, useRef, useState } from "react";
import { Search, MapPin, Loader2, X, Globe } from "lucide-react";

const NOMINATIM = "https://nominatim.openstreetmap.org/search";
const DEBOUNCE_MS = 350;

/**
 * Props:
 *  - onSelect({ lat, lng, label, city, country })
 *  - lang : "fr" | "en"
 *  - autoFocus?: boolean
 *  - placeholder?: string
 *  - className?: string
 */
export default function GlobalLocationSearch({
  onSelect,
  lang = "fr",
  autoFocus = false,
  placeholder,
  className = "",
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(-1);
  const timerRef = useRef(null);
  const wrapRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!q || q.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    timerRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      setLoading(true);
      try {
        const params = new URLSearchParams({
          q: q.trim(),
          format: "json",
          addressdetails: "1",
          "accept-language": lang,
          limit: "6",
        });
        const res = await fetch(`${NOMINATIM}?${params.toString()}`, {
          headers: { "Accept": "application/json" },
          signal: abortRef.current.signal,
        });
        if (!res.ok) throw new Error("geo fetch failed");
        const data = await res.json();
        setResults(Array.isArray(data) ? data : []);
        setOpen(true);
        setFocusIdx(-1);
      } catch (err) {
        if (err.name !== "AbortError") setResults([]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [q, lang]);

  const pick = (r) => {
    const addr = r.address || {};
    const city = addr.city || addr.town || addr.village || addr.municipality || addr.county || "";
    const country = addr.country || "";
    const label = r.display_name || `${city}${country ? ", " + country : ""}`;
    setQ(city ? `${city}${country ? ", " + country : ""}` : label);
    setOpen(false);
    onSelect?.({
      lat: parseFloat(r.lat),
      lng: parseFloat(r.lon),
      label,
      city,
      country,
    });
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setFocusIdx((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setFocusIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && focusIdx >= 0) { e.preventDefault(); pick(results[focusIdx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid="global-location-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8B8B8F]" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => q && results.length > 0 && setOpen(true)}
          autoFocus={autoFocus}
          data-testid="location-search-input"
          placeholder={
            placeholder ||
            (lang === "fr"
              ? "Tapez une ville, une adresse — n'importe où dans le monde"
              : "Type any city, address — anywhere in the world")
          }
          className="w-full pl-10 pr-10 py-2.5 border border-[#E4E1D8] bg-white rounded-md text-sm text-[#1F2937] focus:border-navy outline-none transition"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-navy animate-spin" />}
        {!loading && q && (
          <button
            onClick={() => { setQ(""); setResults([]); setOpen(false); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8B8B8F] hover:text-navy"
            aria-label="Effacer"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <div
          data-testid="location-search-results"
          className="absolute z-40 left-0 right-0 mt-1 border border-[#E4E1D8] bg-white rounded-md shadow-lg overflow-hidden max-h-80 overflow-y-auto"
        >
          {results.map((r, i) => {
            const addr = r.address || {};
            const city = addr.city || addr.town || addr.village || addr.municipality || addr.county || "";
            const country = addr.country || "";
            return (
              <button
                key={r.place_id}
                data-testid={`location-result-${i}`}
                onClick={() => pick(r)}
                onMouseEnter={() => setFocusIdx(i)}
                className={`w-full text-left px-4 py-2.5 flex items-start gap-3 border-b border-[#F1EFE9] last:border-0 transition ${
                  focusIdx === i ? "bg-[#F1EFE9]" : "bg-white hover:bg-[#F8F7F3]"
                }`}
              >
                <MapPin className="h-4 w-4 text-navy mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-navy font-medium truncate">
                    {city || r.display_name.split(",")[0]}
                    {country && <span className="text-[#5B5B62] font-normal">, {country}</span>}
                  </div>
                  <div className="text-[11px] text-[#8B8B8F] truncate">{r.display_name}</div>
                </div>
              </button>
            );
          })}
          <div className="px-4 py-2 border-t border-[#E4E1D8] text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F] inline-flex items-center gap-1.5 w-full">
            <Globe className="h-3 w-3" /> {lang === "fr" ? "Source" : "Source"} · OpenStreetMap · Nominatim
          </div>
        </div>
      )}
    </div>
  );
}
